import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {NbInputModule} from '@nebular/theme';
import {FieldType} from '@ngx-formly/core';
import {Subject, takeUntil} from 'rxjs';

/**
 * Formly-тип для `string[]`-полей (orgSameAs, canonicalKeepQueryKeys): textarea,
 * один элемент на строку. Модель формы — массив строк (пустые/пробельные отброшены).
 *
 * Label рендерим сами (как все кастомные field-types проекта — custom FieldType не
 * получает label от formly-wrapper). Высота textarea подгоняется под контент.
 */
@Component({
  selector: 'app-string-list',
  template: `
    <div class="mb-3">
      <textarea #area nbInput fullWidth
                rows="2"
                [value]="text"
                [placeholder]="props.placeholder || ''"
                (input)="onInput($event)"></textarea>
      <small class="form-text text-muted d-block">
        @if (props.description) {
          {{ props.description }}
        }
        Один элемент на строку
      </small>
    </div>
  `,
  standalone: true,
  imports: [NbInputModule],
})
export class StringListComponent extends FieldType implements OnInit, OnDestroy {
  @ViewChild('area') area: ElementRef<HTMLTextAreaElement>;
  text = '';

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.render(this.formControl.value);
    // Значение может прийти ПОЗЖЕ ngOnInit: страницы вроде SEO-настроек сначала строят
    // форму, а модель патчат после ответа GET. Без подписки textarea навсегда оставалась
    // пустой при непустых данных — и, что хуже, первая же правка пользователя затирала
    // весь невидимый список (onInput пересобирает массив из того, что видно на экране).
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        // Эхо собственной правки пропускаем сравнением значений, а не флагом «я только что
        // писал»: флаг сбрасывался бы только приходом valueChanges, и любой путь без эмиссии
        // (setValue с emitEvent:false, updateOn:'blur') оставил бы его взведённым — следующее
        // внешнее обновление молча потерялось бы.
        //
        // Сравнивать надо с тем, что НАБРАНО в textarea, а не с `text` (это лишь последнее
        // отрендеренное значение). Иначе любой неканонический ввод — хвостовые пробелы,
        // пустые строки — считается «внешним обновлением», и render() переписывает textarea
        // под курсором: пробелы исчезают, каретка прыгает в конец.
        if (this.toText(val) === this.toText(this.parse(this.area?.nativeElement.value))) {
          return;
        }
        this.render(val);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInput(event: Event): void {
    this.formControl.setValue(this.parse((event.target as HTMLTextAreaElement).value));
    this.formControl.markAsTouched();
    this.autosize();
  }

  // parse/toText — обратная пара: текст редактора ↔ модель формы. Канонический вид один,
  // поэтому эхо собственной правки узнаётся сравнением, а не флагом состояния.
  private parse(text: string | undefined): string[] {
    return (text ?? '').split('\n').map(s => s.trim()).filter(Boolean);
  }

  private toText(val: unknown): string {
    return Array.isArray(val) ? val.join('\n') : '';
  }

  private render(val: unknown): void {
    this.text = this.toText(val);
    // autosize после рендера, когда textarea уже в DOM с новым value.
    queueMicrotask(() => this.autosize());
  }

  // Auto-height: сбрасываем height и тянем до scrollHeight (растёт с числом строк).
  private autosize(): void {
    const el = this.area?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }
}
